import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Minimize";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  EXPIRY_MS,
  getHeaderBackgroundColor,
  HEADER_HEIGHT,
  IMPERSONATION_USER_EVENTS,
  PHONE_BEZEL_WIDTH,
  RESIZE_HANDLE_SX,
  WARNING_THRESHOLD_MS,
} from "../constants";
import { useImpersonationMessaging } from "../hooks/useImpersonationMessaging";
import { useSessionTimer } from "../hooks/useSessionTimer";
import { useWindowInteractions } from "../hooks/useWindowInteractions";
import { useImpersonationSessions } from "../ImpersonationSessionsProvider";
import type { ImpersonationSession } from "../types";
import { ClosingOverlay, ErrorOverlay } from "./ImpersonationWindowOverlays";

interface FloatingImpersonationWindowProps {
  session: ImpersonationSession;
}

export function FloatingImpersonationWindow({ session }: FloatingImpersonationWindowProps) {
  const { adminUserId: employeeId } = useSession();
  const {
    bringToFront,
    closeSession,
    minimizeSession,
    updateSessionExpiresAt,
    updateWindowPosition,
    updateWindowSize,
  } = useImpersonationSessions();
  const elapsed = useSessionTimer(session.startedAt);

  const remainingMs = session.expiresAt ? session.expiresAt - Date.now() : EXPIRY_MS - elapsed;
  const isWarning = remainingMs <= WARNING_THRESHOLD_MS && remainingMs > 0;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeKey, setIframeKey] = useState(0);

  const sessionOrigin = useMemo(() => new URL(session.url).origin, [session.url]);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const forceClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeSession(session.id);
  }, [closeSession, session.id]);

  const handleClose = useCallback(() => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);

    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: "impersonation:end" }, sessionOrigin);
    }

    closeTimeoutRef.current = setTimeout(() => {
      forceClose();
    }, 5_000);
  }, [isClosing, sessionOrigin, forceClose]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleExpiresAtUpdate = useCallback(
    (expiresAt: number) => {
      updateSessionExpiresAt(session.id, expiresAt);
    },
    [updateSessionExpiresAt, session.id]
  );

  const { error, resetTokenState } = useImpersonationMessaging({
    employeeId,
    session,
    iframeRef,
    onSessionEnd: handleClose,
    onCleanupComplete: forceClose,
    onExpiresAtUpdate: handleExpiresAtUpdate,
  });

  const handleRefresh = useCallback(() => {
    resetTokenState();
    setIframeKey((previous) => previous + 1);
    logEvent(IMPERSONATION_USER_EVENTS.SESSION_REFRESHED, {
      employeeId,
      workerId: session.workerId,
      sessionId: session.id,
    });
  }, [employeeId, resetTokenState, session.workerId, session.id]);

  const {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
  } = useWindowInteractions({
    sessionId: session.id,
    position: session.position,
    size: session.size,
    updateWindowPosition,
    updateWindowSize,
  });

  const remainingMinutes = Math.max(0, Math.ceil(remainingMs / 60_000));
  const totalWidth = session.size.width + PHONE_BEZEL_WIDTH * 2;
  const headerTitle = isClosing
    ? "Closing..."
    : `${session.workerName} — ${session.workerId} (${remainingMinutes}m)`;

  return (
    <Box
      sx={{
        position: "fixed",
        left: session.position.x,
        top: session.position.y,
        zIndex: (theme) => theme.zIndex.modal + 1 + session.zIndex,
        display: session.isMinimized ? "none" : "flex",
        flexDirection: "column",
        userSelect: "none",
      }}
      onPointerDown={() => {
        bringToFront(session.id);
      }}
    >
      <Tooltip title={isWarning ? "Session expires soon" : ""}>
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            height: HEADER_HEIGHT,
            paddingX: 1.5,
            backgroundColor: getHeaderBackgroundColor(isClosing, error, isWarning),
            color: theme.palette.common.white,
            borderRadius: "8px 8px 0 0",
            cursor: "grab",
            width: totalWidth,
            boxSizing: "border-box",
            transition: "background-color 0.3s",
          })}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
        >
          <Box
            component="span"
            sx={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: 600,
              fontSize: "0.75rem",
              pointerEvents: "none",
            }}
          >
            {headerTitle}
          </Box>
          <IconButton
            size="small"
            aria-label={`Refresh impersonation session for ${session.workerName}`}
            disabled={isClosing}
            sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
            onClick={handleRefresh}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Minimize impersonation session for ${session.workerName}`}
            disabled={isClosing}
            sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
            onClick={() => {
              minimizeSession(session.id);
            }}
          >
            <MinimizeIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Close impersonation session for ${session.workerName}`}
            disabled={isClosing}
            sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
            onClick={handleClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Tooltip>

      <Box
        sx={{
          width: totalWidth,
          height: session.size.height + PHONE_BEZEL_WIDTH * 2,
          border: `${PHONE_BEZEL_WIDTH}px solid`,
          borderColor: "grey.800",
          borderRadius: `0 0 ${40}px ${40}px`,
          overflow: "hidden",
          boxShadow: 24,
          backgroundColor: "background.paper",
          position: "relative",
        }}
      >
        <Box
          key={iframeKey}
          ref={iframeRef}
          component="iframe"
          src={session.url}
          sx={{
            width: "100%",
            height: "100%",
            border: "none",
            display: isClosing || error ? "none" : "block",
          }}
          title={`Impersonation - ${session.workerName}`}
        />
        {isClosing && <ClosingOverlay />}
        {!isClosing && error && <ErrorOverlay error={error} onRetry={handleRefresh} />}
        <Box
          sx={RESIZE_HANDLE_SX}
          onPointerDown={handleResizeStart}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeEnd}
        />
      </Box>
    </Box>
  );
}
